/* ──────────────────────────────────────────────────────────────
   src/hooks/useReservaMutation.ts
   ────────────────────────────────────────────────────────────── */
import { useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../api/axios';
import { enqueueSnackbar } from '../components/utils/snackbar';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { listInfo } from '../components/utils/listinfo';
dayjs.locale('es'); // fechas en español

/* ============ tipos ============ */
type Mode = 'create' | 'edit' | 'recurrente' | 'serieUpdate'; // editar todas las futuras

interface Params {
  mode: Mode;
  /** para edición individual */
  id?: number;
  /** para edición en bloque */
  serieId?: string;
  desde?: string; // YYYY-MM-DD
  /** DTO que espera el backend */
  data: Record<string, unknown>;
}

/* ===================================================================
      HOOK
      =================================================================== */
export function useReservaMutation() {
  const qc = useQueryClient();

  /* ---------- petición única ---------- */
  async function request({ mode, id, serieId, desde, data }: Params) {
    /* ▸ Limpiamos el DTO SOLO cuando se trata de edición en bloque.
          · El backend interpreta campos ausentes como “no modificar”.
          · Se eliminan undefined, null y cadenas vacías ('').          */
    const cleanData =
      mode === 'serieUpdate'
        ? Object.fromEntries(
            Object.entries(data).filter(([, v]) => v !== undefined && v !== null && v !== ''),
          )
        : data;

    const cfg = {
      create: { url: '/reservas', m: 'post' },
      recurrente: { url: '/reservas/recurrentes', m: 'post' },
      edit: { url: `/reservas/${id}`, m: 'patch' },
      serieUpdate: {
        url: `/reservas/recurrentes/${serieId}?desde=${desde}`,
        m: 'patch',
      },
    }[mode];

    const { data: res } = await (api as any)[cfg.m](cfg.url, cleanData);
    return { res, mode } as const;
  }

  /* ---------- react-query ---------- */
  return useMutation({
    mutationFn: request,

    onSuccess: ({ res, mode }) => {
      /* refrescar cualquier rango de reservas en caché */
      qc.invalidateQueries({ queryKey: ['reservas'] });

      /* ===== ALTA DE SERIE ===== */
      if (mode === 'recurrente') {
        const { creadas, fallidas } = res as any;

        enqueueSnackbar(`Serie creada: ${creadas} correctas — ${fallidas.length} con error`, {
          variant: fallidas.length ? 'warning' : 'success',
          autoHideDuration: 6000,
        });

        fallidas.forEach((f: { fecha: string; motivo: string }) =>
          enqueueSnackbar(
            `La reserva del ${dayjs(f.fecha).format('DD/MM/YYYY')} no se creó: ${f.motivo}`,
            { variant: 'error', autoHideDuration: 9000 },
          ),
        );
        return;
      }

      /* ===== EDICIÓN DE SERIE (futuras) ===== */
      if (mode === 'serieUpdate') {
        const { modificadas, fallidas } = res as any;

        enqueueSnackbar(
          `Serie actualizada: ${modificadas} correctas — ${fallidas.length} con error`,
          {
            variant: fallidas.length ? 'warning' : 'success',
            autoHideDuration: 6000,
          },
        );

        fallidas.forEach((f: { fecha: string; motivo: string }) =>
          enqueueSnackbar(
            `La reserva del ${dayjs(f.fecha).format('DD/MM/YYYY')} no se modificó: ${f.motivo}`,
            { variant: 'error', autoHideDuration: 9000 },
          ),
        );
        return;
      }

      /* ===== alta / edición individual ===== */
      enqueueSnackbar('Reserva guardada correctamente', {
        variant: 'success',
      });
    },

    onError: (err: any) => {
      const data = err?.response?.data;
      const nestedMsg = data?.message && typeof data.message === 'object' ? data.message : undefined;
      const detailsContainer =
        data?.details && typeof data.details === 'object' ? data.details : undefined;

      const msg =
        (typeof detailsContainer?.message === 'string' ? detailsContainer.message : undefined) ??
        (typeof data?.message === 'string' ? data.message : undefined) ??
        (typeof nestedMsg?.message === 'string' ? nestedMsg.message : undefined) ??
        (typeof nestedMsg?.mensaje === 'string' ? nestedMsg.mensaje : undefined) ??
        (typeof data === 'string' ? data : undefined) ??
        err?.message ??
        'Error inesperado al guardar la reserva';

      // Algunos errores (p.ej. límite del 30% en pendientes) devuelven el array en
      // `details` o en `fallidas`, a veces anidados en `message`.
      const detailsRaw =
        (data as any)?.details ??
        (data as any)?.fallidas ??
        (data as any)?.data?.fallidas ??
        (nestedMsg as any)?.details ??
        (nestedMsg as any)?.fallidas ??
        (nestedMsg as any)?.data?.fallidas ??
        (detailsContainer as any)?.details ??
        (detailsContainer as any)?.fallidas ??
        (detailsContainer as any)?.data?.fallidas;

      const details = Array.isArray(detailsRaw)
        ? detailsRaw
        : Array.isArray((detailsRaw as any)?.details)
        ? (detailsRaw as any).details
        : Array.isArray((detailsRaw as any)?.fallidas)
        ? (detailsRaw as any).fallidas
        : [];

      if (details.length) {
        const msgTemplate = listInfo(
          msg,
          details.map((d: unknown) => ({
            fecha: (d as any)?.fecha ?? (d as any)?.dia ?? (d as any)?.date ?? '',
            motivo: (d as any)?.motivo ?? (d as any)?.reason ?? (d as any)?.error ?? '',
          })),
        );
        enqueueSnackbar(msgTemplate, {
          variant: 'error',
          autoHideDuration: 9000,
          style: { whiteSpace: 'pre-line' },
        });
        return;
      }

      enqueueSnackbar(msg, {
        variant: 'error',
        autoHideDuration: 6000,
      });
    },
  });
}
